import { useCallback, useRef, useState } from 'react';
import { API_URL, CF_API_URL } from 'constants/api';
import { HttpMethods } from 'constants/http';
import {
  RequestStatus,
  /**
   * @todo Introduce FetchStatus? One shows the state of data, and the other shows if it's fetching.
   * Ex. Perhaps I want to fetch again without removing the old data and showing it loading.
   */
  // FetchStatus
} from 'constants/requests';
import { getViewerToken } from 'services/client/token';
import { buildApiRequestEndpoint } from 'utils/shared/url/buildApiRequestEndpoint';
import { createRequestGate } from 'utils/client/requests/requestGate';
import { isJsonMediaType } from 'utils/shared/http/isJsonMediaType';

/**
 * @todo SHOULD I JUST USE REACT QUERY?
 */
/**
 * @todo Should I make a simple version that just returns the response and manages the loading state?
 */

interface Params {
  id?: string;
  baseUrl?: string;
  endpoint?: string;
  injectHeaders?: () => Promise<object>;
  keepDataOnRefetch?: boolean;
}

interface RequestConfig<TRequestPayload> extends RequestInit {
  endpoint?: string;
  payload?: TRequestPayload;
}

type FetchResponse<TRequestPayload> = {
  data?: TRequestPayload | string;
  isStream: boolean;
  error?: string | null;
  isError: boolean;
};

type UseApiReturnType<TRequestPayload, TResponsePayload> = {
  response: Response | null;
  data: TResponsePayload | null;
  stream: string | null;
  isStreamResponse: boolean;
  status: RequestStatus;
  error: string | null;
  /** Parsed JSON body of the latest failed response, for routes that explain a 4xx. */
  errorData: unknown;
  fetchData: (config?: RequestConfig<TRequestPayload>) => Promise<FetchResponse<TResponsePayload>>;
  post: (config?: RequestConfig<TRequestPayload>) => Promise<FetchResponse<TResponsePayload>>;
  get: (config?: RequestConfig<TRequestPayload>) => Promise<FetchResponse<TResponsePayload>>;
  put: (config?: RequestConfig<TRequestPayload>) => Promise<FetchResponse<TResponsePayload>>;
  patch: (config?: RequestConfig<TRequestPayload>) => Promise<FetchResponse<TResponsePayload>>;
  delete: (config?: RequestConfig<TRequestPayload>) => Promise<FetchResponse<TResponsePayload>>;
  isCalled: boolean;
  RequestStatus: typeof RequestStatus;
  isLoading: boolean;
  isInitialLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  responseStatusCode: number | null;
  responseHeaders: Headers | null;
  stopRequest: () => void;
  resetInitialState: () => void;
};

const injectViewerToken = async () => {
  const token = await getViewerToken();

  if (token) {
    return { Authorization: `Bearer ${token}` };
  } else {
    return {};
  }
};

export const useApi = <TRequestPayload = any, TResponsePayload = any>({
  baseUrl,
  endpoint: endpointPrefix,
  injectHeaders,
  keepDataOnRefetch = false,
}: Params): UseApiReturnType<TRequestPayload, TResponsePayload> => {
  const [status, setStatus] = useState(RequestStatus.Idle);
  const [data, setData] = useState<TResponsePayload | null>(null);
  const [stream, setStream] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorData, setErrorData] = useState<unknown>(null);
  const [isCalled, setIsCalled] = useState(false);
  const [isStreamResponse, setIsStreamResponse] = useState(false);
  const [responseStatusCode, setResponseStatusCode] = useState<number | null>(null);
  const [responseHeaders, setResponseHeaders] = useState<Headers | null>(null);
  const [response, setResponse] = useState<Response | null>(null);
  const abortController = useRef(new AbortController());
  const lastRequestEndpointRef = useRef<string>('');
  /**
   * Which request may write to this hook's state — see `createRequestGate`.
   *
   * The endpoint ref above only decides whether to CLEAR on a new request; it
   * never stopped a superseded one from finishing and committing.
   */
  const requestGate = useRef(createRequestGate());

  const resetInitialState = useCallback(() => {
    setResponse(null);
    setData(null);
    setStream(null);
    setError(null);
    setErrorData(null);
    setIsStreamResponse(false);
    setResponseStatusCode(null);
    setResponseHeaders(null);
    setStatus(RequestStatus.Idle);
    setIsCalled(false);
  }, []);

  const resetNewRequest = (shouldClearData: boolean = true) => {
    setResponse(null);
    if (shouldClearData) {
      setData(null);
    }
    setStream(null);
    setError(null);
    setErrorData(null);
    setIsStreamResponse(false);
    setResponseStatusCode(null);
    setResponseHeaders(null);
    setStatus(RequestStatus.InProgress);
  };

  // Function to handle the API request
  const fetchData = async ({
    endpoint: fetchEndpoint,
    payload,
    ...customConfig
  }: RequestConfig<TRequestPayload> = {}) => {
    /**
     * @todo Implement some kind of refetching where it doesn't clobber the old data. May need to introduce a FetchStatus.
     */

    // Compute the full request endpoint before resetting state.
    const fullRequestEndpoint = buildApiRequestEndpoint({
      baseUrl,
      endpointPrefix,
      fetchEndpoint,
    });

    // Detect if this is a different request (e.g., different ticker)
    const isDifferentRequest = lastRequestEndpointRef.current !== fullRequestEndpoint;
    lastRequestEndpointRef.current = fullRequestEndpoint;

    // Only preserve data if:
    // 1. keepDataOnRefetch is true (opt-in for polling scenarios)
    // 2. AND it's the same request (same endpoint)
    const shouldClearData = !keepDataOnRefetch || isDifferentRequest;

    // Claim this hook's state. Anything already in flight is now superseded and
    // may no longer write. The caller's own promise still resolves with its own
    // result — only the SHARED state is guarded.
    const isCurrent = requestGate.current.claim();

    resetNewRequest(shouldClearData);
    setIsCalled(true);

    try {
      const config: RequestInit = {
        ...customConfig,
        headers: {
          ...customConfig.headers,
        },
      };

      if (injectHeaders) {
        const injectedHeaders = await injectHeaders();
        config.headers = {
          ...config.headers,
          ...injectedHeaders,
        };
      }

      if (payload) {
        if (payload instanceof FormData) {
          config.body = payload;
        } else {
          config.body = JSON.stringify(payload);
          config.headers = { ...config.headers, 'content-type': 'application/json' };
        }
      }

      /**
       * @todo Should I pick between the endpoints or combine them?
       */

      const response = await fetch(fullRequestEndpoint, {
        ...config,
        signal: abortController.current.signal,
      });

      if (isCurrent()) {
        setResponse(response);
        setResponseStatusCode(response.status);
        setResponseHeaders(response.headers);
      }

      const contentType = response.headers.get('Content-Type');
      const isStream = !!contentType && contentType.includes('text/event-stream');
      const isJson = isJsonMediaType(contentType);

      if (!response.ok) {
        if (isJson) {
          // Accept `error` as well as `message`. The GEX Worker reports a
          // recovering origin as `503 { status: 'recovering', error: '...' }`;
          // reading only `message` collapsed that into the bare status text, so
          // a deliberately structured, explanatory failure reached the user as
          // "Service Unavailable" — the same opaque outcome the structured body
          // was introduced to replace.
          const body: { message?: string; error?: string } = await response.json();
          if (isCurrent()) setErrorData(body);
          throw new Error(body.message || body.error || `${response.statusText}`);
        } else {
          const errorText = await response.text();
          throw new Error(errorText || `${response.statusText}`);
        }
      }

      if (isCurrent()) setIsStreamResponse(isStream);

      if (isStream) {
        const data = response.body;
        const reader = data?.getReader();
        const decoder = new TextDecoder();
        let result = '';
        let done = false;

        if (reader) {
          while (!done) {
            const { value, done: doneReading } = await reader.read();
            done = doneReading;
            const chunkValue = decoder.decode(value);
            result += chunkValue;
            // @ts-ignore It will be a string if it's streaming, how do I tell TS that?
            // setData((prev) => prev + chunkValue);
            /**
             * @todo Pass in a handleStream function that handles the stream response? Maybe you need to JSON.parse or something.
             */
            if (isCurrent()) setStream((prev) => (prev || '') + chunkValue);
          }
        }

        if (isCurrent()) setStatus(RequestStatus.Success);

        return { data: result, isStream, isError: false };
      } else if (isJson) {
        const result: TResponsePayload = await response.json();
        if (isCurrent()) {
          setData(result);
          setStatus(RequestStatus.Success);
        }

        return { data: result, isStream, isError: false };
      } else {
        const result = await response.text();
        if (isCurrent()) setStatus(RequestStatus.Success);

        return { data: result, isStream, isError: false };
      }
    } catch (e) {
      const error = e as Error;
      // A superseded request's FAILURE must not surface either: a stale error
      // would blank a chart the current request had already filled.
      if (isCurrent()) {
        setError(error.message);
        setStatus(RequestStatus.Error);
      }

      return { error: error.message, isStream: false, isError: true };
    }
  };

  const stopRequest = useCallback(() => {
    abortController.current.abort();
    abortController.current = new AbortController();
  }, []);

  return {
    response,
    data,
    stream,
    isStreamResponse,
    status,
    error,
    errorData,
    fetchData,
    post: ({ payload, ...rest }: RequestConfig<TRequestPayload> = {}) =>
      fetchData({ ...rest, method: HttpMethods.Post, payload }),
    get: ({ payload, ...rest }: RequestConfig<TRequestPayload> = {}) =>
      fetchData({ ...rest, method: HttpMethods.Get, payload }),
    put: ({ payload, ...rest }: RequestConfig<TRequestPayload> = {}) =>
      fetchData({ ...rest, method: HttpMethods.Put, payload }),
    patch: ({ payload, ...rest }: RequestConfig<TRequestPayload> = {}) =>
      fetchData({ ...rest, method: HttpMethods.Patch, payload }),
    delete: ({ payload, ...rest }: RequestConfig<TRequestPayload> = {}) =>
      fetchData({ ...rest, method: HttpMethods.Delete, payload }),
    isCalled,
    RequestStatus,
    isLoading: status === RequestStatus.InProgress,
    isInitialLoading: status === RequestStatus.InProgress && data === null,
    isSuccess: status === RequestStatus.Success,
    isError: status === RequestStatus.Error,
    responseStatusCode,
    responseHeaders,
    stopRequest,
    resetInitialState,
  };
};

export const useApiGateway = <TRequestPayload = any, TResponsePayload = any>(
  endpoint?: string,
  options?: Omit<Params, 'endpoint' | 'baseUrl' | 'injectHeaders'>,
): UseApiReturnType<TRequestPayload, TResponsePayload> => {
  return useApi<TRequestPayload, TResponsePayload>({
    endpoint,
    baseUrl: API_URL,
    injectHeaders: injectViewerToken,
    ...options,
  });
};

/**
 * Mirror of `useApiGateway` that targets the Cloudflare Workers API
 * (`cf.insiderfinance.io` in prod, `cf-staging.insiderfinance.io` in staging).
 * Used by features that have a Worker companion — currently GEX. Falls back
 * to the in-app /api gateway when CF_API_URL is unset, so a missing env var
 * doesn't break the page.
 *
 * Token injection is identical to `useApiGateway` even though the Worker
 * doesn't currently validate tokens — keeps the call shape consistent if/when
 * we add auth, and the Worker harmlessly ignores headers it doesn't read.
 */
export const useCfApiGateway = <TRequestPayload = any, TResponsePayload = any>(
  endpoint?: string,
  options?: Omit<Params, 'endpoint' | 'baseUrl' | 'injectHeaders'>,
): UseApiReturnType<TRequestPayload, TResponsePayload> => {
  return useApi<TRequestPayload, TResponsePayload>({
    endpoint,
    baseUrl: CF_API_URL,
    injectHeaders: injectViewerToken,
    ...options,
  });
};
